<template>
  <div class="stats-page">
    <!-- Page Header -->
    <h2 class="title">
      {{ $t('userAdmin.statsHeader') }}
    </h2>

    <!-- Info Text -->
    <div class="infotext">
      {{ $t('userAdmin.statsInfo1') }}
      <span class="highlight">{{ statsData.currentVolunteers }}</span>
      {{ $t('userAdmin.statsInfo2') }}
      <span class="highlight">{{ statsData.currentActiveVolunteers }}</span>
      {{ $t('userAdmin.statsInfo5') }}
      <br />
      {{ $t('userAdmin.genderDistribution') }}
      <span class="highlight"
        >{{ statsData.volunteerGenderDistribution.female }}%</span
      >
      {{ $t('userAdmin.genderDistributionFemale') }},
      <span class="highlight"
        >{{ statsData.volunteerGenderDistribution.male }}%</span
      >{{ $t('userAdmin.genderDistributionMale') }},
      <span class="highlight"
        >{{ statsData.volunteerGenderDistribution.other }}%</span
      >
      {{ $t('userAdmin.genderDistributionOther') }}.

      <br />
      <br />
      {{ $t('userAdmin.statsInfo3') }}
      <span class="highlight">{{ statsData.currentCustomers }}</span>
      {{ $t('userAdmin.statsInfo4') }}
      <span class="highlight">{{ statsData.currentActiveCustomers }}</span>
      {{ $t('userAdmin.statsInfo5') }}
      <br />
      {{ $t('userAdmin.genderDistribution') }}
      <span class="highlight"
        >{{ statsData.customerGenderDistribution.female }}%</span
      >
      {{ $t('userAdmin.genderDistributionFemale') }},
      <span class="highlight"
        >{{ statsData.customerGenderDistribution.male }}%</span
      >
      {{ $t('userAdmin.genderDistributionMale') }},
      <span class="highlight"
        >{{ statsData.customerGenderDistribution.other }}%</span
      >
      {{ $t('userAdmin.genderDistributionOther') }}.
    </div>

    <div class="stats-content">
      <!-- Left Column -->
      <div class="left-column">
        <div class="button-row">
          <SprButton
            :size="4"
            :title="$t('userAdmin.statsVolunteers')"
            click-event="volunteers"
            @volunteers="selectView('volunteers')"
          />

          <!-- NEW BUTTON next to Volunteers -->
          <SprButton
            :size="4"
            :title="$t('userAdmin.statsActiveVolunteers')"
            click-event="activevolunteers"
            @activevolunteers="selectView('activeVolunteers')"
          />
        </div>

        <!-- ROW: Customers -->
        <div class="button-row">
          <SprButton
            :size="4"
            :title="$t('userAdmin.statsCustomers')"
            click-event="customers"
            @customers="selectView('customers')"
          />

          <!-- NEW BUTTON next to Customers -->
          <SprButton
            :size="4"
            :title="$t('userAdmin.statsActiveCustomers')"
            click-event="activecustomers"
            @activecustomers="selectView('activeCustomers')"
          />
        </div>
        <br />

        <SprButton
          :size="4"
          :title="$t('userAdmin.statsFriendPairs')"
          click-event="pairs"
          @pairs="selectView('pairs')"
        />
        <br />

        <SprButton
          :size="4"
          :title="$t('userAdmin.statsGroupMembers')"
          click-event="groupMembers"
          @groupMembers="selectView('groupMembers')"
        />
        <br />

        <div class="free-interval">
          <h3>{{ $t('userAdmin.selectFreeInterval') }}</h3>

          <div class="datepickers">
            <SPRDatepicker
              v-model="startDate"
              :initial="startDate"
              :title="$t('userAdmin.startDate')"
              :label="$t('userAdmin.startDate')"
              :placeholder="$t('userAdmin.selectDate')"
              :options="datepickerOptions"
              :limit="startDateLimit"
            />
            <SPRDatepicker
              v-model="endDate"
              :initial="endDate"
              :title="$t('userAdmin.endDate')"
              :label="$t('userAdmin.endDate')"
              :placeholder="$t('userAdmin.selectDate')"
              :options="datepickerOptions"
              :limit="endDateLimit"
            />
          </div>

          <div class="year-buttons">
            <SprButton
              :size="4"
              fullwidth
              :title="$t('userAdmin.showPreviousYear')"
              click-event="previousYear"
              @previousYear="selectYear('previous')"
            />
            <SprButton
              :size="4"
              fullwidth
              :title="$t('userAdmin.showCurrentYear')"
              click-event="currentYear"
              @currentYear="selectYear('current')"
            />
          </div>
        </div>
      </div>

      <!-- Right Column -->
      <div class="right-column">
        <div class="info-box">
          <FriendStatBox
            :selectedData="getSelectedLabel(selectedView)"
            :startDate="formatDateForApi(startDate)"
            :endDate="formatDateForApi(endDate)"
            :startValue="statsData.startValue"
            :endValue="statsData.endValue"
          />
        </div>
      </div>

      <div v-show="loading" class="loading">
        <div class="spinner">
          <loading />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import loading from '../SPRLoading.vue'
import cache from '../../mixins/cache.js'
import SprButton from '../SPRButton.vue'
import SPRDatepicker from '../SPRDatepicker.vue'
import moment from 'moment'
import FriendStatBox from './FriendStatBox.vue'

import {
  FriendPoolStandardDto,
  FriendPoolNetDto,
  FriendManagementDto,
  GenderDistributionDto
} from '@/types/index'

// the data object
export interface TemplateComponentData {
  loading: boolean
  loaded: number
  pools: Array<FriendPoolStandardDto | FriendPoolNetDto> | undefined
  friendshipGroup: FriendManagementDto
  datepickerOptions: Record<string, any>
  startDate: string
  endDate: string
  statsData: {
    startValue: number
    endValue: number
    currentCustomers: number
    currentVolunteers: number
    currentActiveCustomers: number
    currentActiveVolunteers: number
    customerGenderDistribution: GenderDistributionDto
    volunteerGenderDistribution: GenderDistributionDto
  }
  selectedView: string
}

export default Vue.extend({
  name: 'FriendManagementGroupStatistics',
  components: {
    loading,
    SprButton,
    FriendStatBox,
    SPRDatepicker
  },
  mixins: [cache],
  props: {
    primary: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    filter: {
      type: String,
      default: ''
    }
  },
  data(): TemplateComponentData {
    return {
      loading: false,
      pools: undefined,
      friendshipGroup: {
        id: 0,
        name: this.$t('userAdmin.toFriendshipLink').toString(),
        contactInfo: [],
        groupActivities: [],
        national: false,
        volunteerCount: 0,
        customerCount: 0,
        friendRequestFormAllowed: false,
        sections: [],
        zipCodes: [],
        netFriendship: false
      },
      statsData: {
        startValue: 0,
        endValue: 0,
        currentCustomers: 0,
        currentVolunteers: 0,
        currentActiveCustomers: 0,
        currentActiveVolunteers: 0,
        customerGenderDistribution: {} as GenderDistributionDto,
        volunteerGenderDistribution: {} as GenderDistributionDto
      },
      selectedView: 'volunteers',
      loaded: 0,
      // ✅ Default to current year start and today in DD.MM.YYYY
      startDate: moment().startOf('year').format('DD.MM.YYYY'),
      endDate: moment().format('DD.MM.YYYY'),
      datepickerOptions: {
        inputStyle: {
          display: 'inline-block',
          padding: '10px 20px',
          'line-height': '22px',
          'font-size': '16px',
          border: '2px solid #DDDDDD',
          'box-shadow': 'none',
          'border-radius': '24px',
          color: '#5F5F5F',
          width: '100%'
        }
      }
    }
  },
  computed: {
    // End date cannot be earlier than startDate, and cannot be after today
    endDateLimit() {
      const from = this.startDate
        ? moment(this.startDate, ['DD.MM.YYYY', 'YYYY-MM-DD']).format(
            'YYYY-MM-DD'
          )
        : '1900-01-01'

      const to = moment()
        .add(1, 'day') // isBetween() used in BaseDatePicker.vue is exclusive, so this allows selecting today
        .format('YYYY-MM-DD')
      return [{ type: 'fromto', from, to }]
    },

    // Start date cannot be after endDate (if endDate exists). Otherwise allow very old dates.
    startDateLimit() {
      const from = '1900-01-01'
      const to = this.endDate
        ? moment(this.endDate, ['DD.MM.YYYY', 'YYYY-MM-DD']).format(
            'YYYY-MM-DD'
          )
        : moment().format('YYYY-MM-DD') // optionally restrict start to <= today
      return [{ type: 'fromto', from, to }]
    }
  },
  watch: {
    startDate(newStart) {
      if (!this.endDate) return
      const s = moment(newStart, ['DD.MM.YYYY', 'YYYY-MM-DD'])
      const e = moment(this.endDate, ['DD.MM.YYYY', 'YYYY-MM-DD'])
      if (s.isAfter(e, 'day')) {
        // user moved start beyond current end — clear end so user must re-pick
        this.endDate = ''
      }
      this.updateStats()
    },
    endDate(newEnd) {
      if (!this.startDate || !newEnd) return
      const s = moment(this.startDate, ['DD.MM.YYYY', 'YYYY-MM-DD'])
      const e = moment(newEnd, ['DD.MM.YYYY', 'YYYY-MM-DD'])
      if (e.isBefore(s, 'day')) {
        // shouldn't happen if limits work, but guard anyway
        this.startDate = ''
      }
      this.updateStats()
    }
  },
  async mounted(): Promise<void> {
    //this.randomizeStats()

    await this.triggerFetch()
  },
  methods: {
    selectView(view: string): void {
      this.selectedView = view

      // Optionally refresh stats data per view
      this.fetchStatistics()
      //this.randomizeStats()
    },
    async fetchStatistics() {
      try {
        this.loading = true

        const payload = {
          startDate: this.formatDateForApi(this.startDate),
          endDate: this.formatDateForApi(this.endDate),
          friendManagementGroupId: this.id,
          dataType: this.selectedView // "volunteers", "customers", etc.
        }

        let endpointKey = this.friendshipGroup.netFriendship
          ? 'getNetStatistics'
          : 'getStatistics'
        const url =
          this.$store.state.apiBases.friendservice +
          this.$store.state.apiEndpoints[endpointKey][0]

        const response = await this.$http.post(url, payload)

        if (response && response.data) {
          this.statsData.startValue = response.data.countStart
          this.statsData.endValue = response.data.countEnd
          this.statsData.currentVolunteers = response.data.currentVolunteerCount
          this.statsData.currentCustomers = response.data.currentCustomerCount
          this.statsData.currentActiveVolunteers =
            response.data.currentActiveVolunteerCount
          this.statsData.currentActiveCustomers =
            response.data.currentActiveCustomerCount
          this.statsData.customerGenderDistribution =
            response.data.customerGenderDistribution || {}
          this.statsData.volunteerGenderDistribution =
            response.data.volunteerGenderDistribution || {}
        }
      } catch (err) {
        console.error('Error fetching statistics:', err)
        this.displayError(err)
      } finally {
        this.loading = false
      }
    },
    getSelectedLabel(view: string): string {
      switch (view) {
        case 'volunteers':
          return this.$t('userAdmin.statsVolunteers') as string
        case 'customers':
          return this.$t('userAdmin.statsCustomers') as string
        case 'pairs':
          return this.$t('userAdmin.statsFriendPairs') as string
        case 'groupMembers':
          return this.$t('userAdmin.statsGroupMembers') as string
        case 'activeVolunteers':
          return this.$t('userAdmin.statsActiveVolunteers') as string
        case 'activeCustomers':
          return this.$t('userAdmin.statsActiveCustomers') as string
        default:
          return this.$t('userAdmin.statsVolunteers') as string
      }
    },
    updateStats() {
      if (!this.startDate || !this.endDate || !this.id) return

      this.fetchStatistics()
      const start = this.formatDateForApi(this.startDate)
      const end = this.formatDateForApi(this.endDate)

      // Replace this with your actual API call or computed logic
      // Generate some demo data
      //this.randomizeStats()
    },
    formatDateForApi(dateString: string): string {
      // Convert DD.MM.YYYY → YYYY-MM-DD
      return moment(dateString, ['DD.MM.YYYY', 'YYYY-MM-DD']).format(
        'YYYY-MM-DD'
      )
    },
    randomizeStats() {
      const base = Math.floor(Math.random() * 1000)
      const randomize = Math.random() < 0.5 ? -1 : 1

      // Limit diff so it can’t exceed base (prevents going below 0)
      const maxDiff = Math.min(200, base)
      const diff = Math.floor(Math.random() * maxDiff) * randomize

      this.statsData.startValue = base
      this.statsData.endValue = base + diff
    },
    selectYear(year: string): void {
      const now = moment()
      if (year === 'current') {
        this.startDate = moment().startOf('year').format('DD.MM.YYYY')
        this.endDate = now.format('DD.MM.YYYY')
      } else if (year === 'previous') {
        const prev = now.clone().subtract(1, 'year')
        this.startDate = prev.startOf('year').format('DD.MM.YYYY')
        this.endDate = prev.endOf('year').format('DD.MM.YYYY')
      }
    },
    triggerFetch: function () {
      this.loaded = 0
      // Guard against missing id
      if (!this.id) {
        console.warn('FriendManagementGroupStatistics: missing id prop')
        this.loaded = -1
        return
      }
      // Properly construct URL (previously left in expression, never assigned)
      const url =
        this.$store.state.apiBases.friendservice +
        this.$store.state.apiEndpoints.friendOrganizationById[0].replace(
          '{0}',
          this.id
        )

      const self = this
      this.fetchItem(
        url,
        this.expSeconds(120),
        (s) => {
          self.friendshipGroup = s
          self.loaded = 1
          self.fetchStatistics()
        },
        () => {
          self.loaded = -1
        }
      )
    },
    displayError(err: Response | any): void {
      if (Array.isArray(err.body)) {
        err.body.forEach((x) => {
          // @ts-ignore
          this.showAlertError(x.errorMessage)
        })
      } else if (err.body && err.body.errorMessage) {
        // @ts-ignore
        this.showAlertError(err.errorMessage)
      } else if (err.bodyText) {
        // @ts-ignore
        this.showAlertError(err.bodyText)
      } else {
        // @ts-ignore
        this.showAlertError(this.$t('common.fetchFailed'))
      }
    }
  }
})
</script>
<style lang="scss" scoped>
@import '~@/assets/scss/_variables.scss';

.stats-page {
  background-color: $brand-grey1;
  padding: 1.5rem 2rem;
  color: $brand-font;
  font-family: 'Signa-Regular', sans-serif;

  .title {
    font-family: 'Signa-Bold', sans-serif;
    color: $brand-red;
    font-size: 1.5rem;
    margin-bottom: 1rem;
    line-height: 1.3;
  }

  .infotext {
    color: $brand-grey12;
    font-size: 1rem;
    margin-bottom: 2rem;
    line-height: 1.5;

    .highlight {
      font-weight: bold;
      color: $brand-black;
      margin: 0 0.25rem;
    }
  }

  .stats-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1.5rem;
  }

  .left-column {
    flex: 1 1 220px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;

    .year-buttons {
      margin-top: 1rem;
    }
    .button-row {
      display: flex;
      gap: 1rem;
      width: 100%;

      ::v-deep(button) {
        flex: 1;
        justify-content: center;
        width: 100%;
      }
    }
    .free-interval {
      margin-top: 1.5rem;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;

      h3 {
        font-size: 1.1rem;
        font-family: 'Signa-Bold', sans-serif;
        color: $brand-red;
        margin-bottom: 0.25rem;
      }

      .datepickers {
        display: flex;
        gap: 1rem;
        flex-wrap: nowrap;
        width: 100%;

        ::v-deep(.datepicker-container) {
          flex: 1;
          width: 100%;
        }
      }

      .year-buttons {
        display: flex;
        gap: 1rem;
        width: 100%;

        ::v-deep(button) {
          flex: 1;
          width: 100%;
          justify-content: center;
        }
      }
    }
  }

  .right-column {
    flex: 1 1 320px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .info-box {
      width: 100%;
      max-width: 600px;
      height: 375px;
      border: 2px solid $brand-red;
      border-radius: 16px;
      background-color: $brand-white;
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;

      .info-text {
        color: $brand-grey13;
        font-family: 'Signa-Bold', sans-serif;
        font-size: 1.25rem;
      }
    }

    .current-year-btn {
      margin-top: 1rem;
      display: flex;
      justify-content: center;
      width: 100%;
    }
  }
}
</style>
